import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { ConsultoresSupabaseService } from './consultoresService';

type PendingUser = Database['public']['Tables']['profiles']['Row'] & {
  email?: string;
  created_at: string;
  status?: string;
};

type Tenant = Database['public']['Tables']['tenants']['Row'];

export class UsuariosPendentesSupabaseService {
  /**
   * Busca usuários pendentes de aprovação
   */
  static async listarUsuariosPendentes(tenantId?: string | number): Promise<PendingUser[]> {
    try {
      // Buscar profiles com status PENDING (usuários pendentes)
      let query = supabase
        .from('profiles')
        .select(`
          id,
          email,
          first_name,
          last_name,
          role,
          status,
          tenant_id,
          created_at,
          updated_at,
          is_superadmin
        `)
        .eq('status', 'PENDING')
        .order('created_at', { ascending: false });

      if (tenantId != null && String(tenantId).trim() !== '') {
        query = query.eq('tenant_id', Number(tenantId));
      }

      const { data: profiles, error: profilesError } = await query;

      if (profilesError) {
        console.error('Erro ao buscar profiles:', profilesError);
        throw profilesError;
      }

      if (!profiles || profiles.length === 0) {
        return [];
      }

      // Retornar profiles com email e status
      const pendingUsers: PendingUser[] = profiles.map((profile: any) => ({
        ...profile,
        status: 'PENDING' as const
      }));

      return pendingUsers;
    } catch (error) {
      console.error('Erro no serviço de usuários pendentes:', error);
      throw error;
    }
  }

  /**
   * Busca tenants ativos
   */
  static async listarTenants(): Promise<Tenant[]> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('status', 'ATIVO')
        .order('nome', { ascending: true });

      if (error) {
        console.error('Erro ao buscar tenants:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Erro no serviço de tenants:', error);
      throw error;
    }
  }

  /**
   * Aprova um usuário pendente
   */
  static async aprovarUsuario(
    userId: string, 
    tenantId: string, 
    role: string,
    supervisorId?: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      const { resolveRoleForTenantId } = await import(
        '@/features/superadmin/utils/tenantRoleResolver'
      );
      const resolvedRole = await resolveRoleForTenantId(tenantId, role);

      const { error } = await supabase
        .from('profiles')
        .update({
          tenant_id: tenantId,
          role: resolvedRole,
          status: 'ACTIVE',
          is_superadmin: resolvedRole === 'SUPER_ADMIN',
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Erro ao aprovar usuário:', error);
        throw error;
      }

      // Se o role for um dos que precisa de consultor, criar o consultor
      const rolesQuePrecisamConsultor = ['ASSOCIADO', 'SUPERVISOR', 'RELACIONADOR', 'LIDER_VENDAS', 'SUPER_ADMIN'];
      
      if (rolesQuePrecisamConsultor.includes(resolvedRole)) {
        // Buscar o profile atualizado
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (profileError || !profile) {
          console.error('Erro ao buscar profile atualizado:', profileError);
          throw new Error('Erro ao buscar profile atualizado');
        }

        // Verificar se já existe um consultor para este profile
        const consultorExiste = await ConsultoresSupabaseService.consultorExisteParaProfile(userId);
        
        if (!consultorExiste) {
          // Criar o consultor
          const consultorResult = await ConsultoresSupabaseService.criarConsultorDeProfile(
            profile,
            supervisorId
          );

          if (!consultorResult.success) {
            console.warn('Aviso: Usuário aprovado mas falha ao criar consultor:', consultorResult.message);
            return {
              success: true,
              message: `Usuário aprovado com sucesso, mas houve um aviso: ${consultorResult.message}`
            };
          }
        }
      }

      return {
        success: true,
        message: 'Usuário aprovado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao aprovar usuário:', error);
      throw error;
    }
  }

  /**
   * Rejeita um usuário pendente
   */
  static async rejeitarUsuario(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      // Atualizar o status para REJECTED em vez de deletar
      const { error } = await supabase
        .from('profiles')
        .update({
          status: 'REJECTED',
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Erro ao rejeitar usuário:', error);
        throw error;
      }

      return {
        success: true,
        message: 'Usuário rejeitado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao rejeitar usuário:', error);
      throw error;
    }
  }

  /**
   * Atualiza o tenant de um usuário
   */
  static async atualizarTenantUsuario(
    userId: string, 
    tenantId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ tenant_id: tenantId })
        .eq('id', userId);

      if (error) {
        console.error('Erro ao atualizar tenant do usuário:', error);
        throw error;
      }

      return {
        success: true,
        message: 'Tenant atualizado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao atualizar tenant do usuário:', error);
      throw error;
    }
  }

  /**
   * Atualiza o role de um usuário
   */
  static async atualizarRoleUsuario(
    userId: string, 
    role: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          role: role,
          is_superadmin: role === 'SUPER_ADMIN'
        })
        .eq('id', userId);

      if (error) {
        console.error('Erro ao atualizar role do usuário:', error);
        throw error;
      }

      return {
        success: true,
        message: 'Role atualizado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao atualizar role do usuário:', error);
      throw error;
    }
  }
}
